import { useEffect, useRef, useState } from "react";
import { X, Copy, CheckCircle2, Loader2, QrCode, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import { api, formatBRL, formatApiError } from "../../lib/api";
import { buildWhatsAppLink } from "./Header";

export default function CheckoutMP({ order, publicKey, whatsappNumber, onClose, onPaid }) {
  const [tab, setTab] = useState("pix");
  const [pix, setPix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paid, setPaid] = useState(false);
  const [cardMessage, setCardMessage] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    initMercadoPago(publicKey, { locale: "pt-BR" });
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = (paymentId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/${paymentId}/status`);
        if (data.status === "approved") {
          clearInterval(pollRef.current);
          setPaid(true);
          onPaid();
        } else if (["rejected", "cancelled", "canceled", "expired"].includes(data.status)) {
          clearInterval(pollRef.current);
          toast.error("Pagamento não aprovado. Tente novamente.");
        }
      } catch {
        // mantém o polling
      }
    }, 4000);
  };

  const generatePix = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/pix", { order_id: order.id, email: order.customer_email });
      setPix(data);
      startPolling(data.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onCardSubmit = async (param) => {
    const formData = param?.formData || param;
    setCardMessage("");
    try {
      const { data } = await api.post("/payments/card", {
        order_id: order.id,
        email: formData?.payer?.email || order.customer_email,
        token: formData.token,
        payment_method_id: formData.payment_method_id,
        installments: formData.installments || 1,
      });
      if (data.status === "approved") {
        setPaid(true);
        onPaid();
      } else if (data.status === "in_process" || data.status === "pending") {
        setCardMessage("Pagamento em análise — avisamos assim que for aprovado.");
        startPolling(data.id);
      } else {
        setCardMessage("Pagamento não aprovado. Confira os dados ou tente outro cartão.");
      }
    } catch (err) {
      setCardMessage(formatApiError(err));
      throw err;
    }
  };

  const copyPix = () => {
    navigator.clipboard.writeText(pix.qr_code);
    toast.success("Código Pix copiado!");
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="checkout-mp-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-[var(--site-bg)] w-full max-w-md max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl border border-[#E8E2D9] p-6">
        {paid ? (
          <div className="text-center py-8" data-testid="payment-success">
            <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="font-display font-bold text-2xl mb-2">Pagamento aprovado!</h2>
            <p className="text-sm text-[#5A534C] mb-6">
              Recebemos seu pedido de {formatBRL(order.total)}. A loja já foi avisada e vai preparar tudo para você.
            </p>
            <div className="flex flex-col gap-3 items-center">
              {whatsappNumber && (
                <a
                  href={buildWhatsAppLink(whatsappNumber, `Pagamento aprovado! Pedido #${order.id.slice(0, 8)} — Total ${formatBRL(order.total)}. Pode separar meu pedido?`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="payment-success-whatsapp-button"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-3.5 rounded-full font-display text-sm font-semibold hover:opacity-90 transition-opacity"
                >
                  Avisar a loja no WhatsApp
                </a>
              )}
              <button
                type="button"
                onClick={onClose}
                data-testid="payment-success-close"
                className="bg-[#1A1816] text-white px-8 py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand)] transition-colors"
              >
                Concluir
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-display font-bold text-xl">Pagamento Online</h2>
              <button type="button" onClick={onClose} data-testid="checkout-close-button" className="p-2 rounded-full hover:bg-[#F2EDE4] transition-colors" aria-label="Fechar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-[#5A534C] mb-5">
              Pedido #{order.id.slice(0, 8)} • Total <span className="font-semibold text-[#1A1816]">{formatBRL(order.total)}</span>
            </p>

            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setTab("pix")}
                data-testid="pay-tab-pix"
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-medium transition-colors ${
                  tab === "pix" ? "bg-[#1A1816] text-white" : "border border-[#D5CDC2] text-[#5A534C] hover:border-[#1A1816]"
                }`}
              >
                <QrCode className="w-4 h-4" /> Pix
                <span className={`text-[9px] font-mono normal-case px-1.5 py-0.5 rounded ${tab === "pix" ? "bg-green-500 text-white" : "bg-green-100 text-green-700"}`}>
                  sem taxa
                </span>
              </button>
              <button
                type="button"
                onClick={() => setTab("card")}
                data-testid="pay-tab-card"
                className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-medium transition-colors ${
                  tab === "card" ? "bg-[#1A1816] text-white" : "border border-[#D5CDC2] text-[#5A534C] hover:border-[#1A1816]"
                }`}
              >
                <CreditCard className="w-4 h-4" /> Cartão
              </button>
            </div>

            {tab === "pix" && (
              <div data-testid="pix-panel">
                {!pix ? (
                  <div className="text-center py-4">
                    <p className="text-sm text-[#5A534C] mb-5">
                      Gere o QR Code e pague na hora pelo app do seu banco. A confirmação é automática.
                    </p>
                    <button
                      type="button"
                      onClick={generatePix}
                      disabled={loading}
                      data-testid="generate-pix-button"
                      className="inline-flex items-center gap-2 bg-[var(--brand)] text-white px-8 py-3.5 rounded-full font-display text-sm font-semibold hover:bg-[var(--brand-dark)] transition-colors disabled:opacity-60"
                    >
                      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {loading ? "Gerando..." : "Gerar QR Code Pix"}
                    </button>
                  </div>
                ) : (
                  <div className="text-center" data-testid="pix-qr-display">
                    {pix.qr_code_base64 && (
                      <img
                        src={`data:image/png;base64,${pix.qr_code_base64}`}
                        alt="QR Code Pix"
                        className="w-52 h-52 mx-auto mb-4 rounded-xl border border-[#E8E2D9] bg-white p-2"
                      />
                    )}
                    <button
                      type="button"
                      onClick={copyPix}
                      data-testid="copy-pix-button"
                      className="inline-flex items-center gap-2 border border-[#D5CDC2] px-5 py-2.5 rounded-full font-display text-xs uppercase tracking-wider font-medium hover:border-[#1A1816] transition-colors mb-4"
                    >
                      <Copy className="w-4 h-4" /> Copiar código Pix
                    </button>
                    <p className="text-xs text-[#6B645C] flex items-center justify-center gap-2">
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--brand)]" />
                      Aguardando o pagamento... a tela atualiza sozinha.
                    </p>
                  </div>
                )}
              </div>
            )}

            {tab === "card" && (
              <div data-testid="card-panel">
                <p className="text-xs text-[#6B645C] mb-2">
                  Preencha os dados do cartão e escolha as parcelas — o valor de cada parcela já aparece com as taxas.
                </p>
                <p className="text-xs bg-green-50 text-green-700 border border-green-200 rounded-xl px-3 py-2 mb-4">
                  Dica: no Pix você não paga taxa e a confirmação é na hora.
                </p>
                <CardPayment
                  initialization={{ amount: order.total, payer: { email: order.customer_email } }}
                  onSubmit={onCardSubmit}
                  onError={(err) => console.error("Brick error:", err)}
                />
                {cardMessage && (
                  <p className="text-sm text-[#5A534C] mt-4 text-center" data-testid="card-payment-message">
                    {cardMessage}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
